import { z } from 'zod';
import { IsoDateSchema } from './common';

export const SubscriptionBillingCycleSchema = z.enum(['monthly', 'annual']);
export type SubscriptionBillingCycle = z.infer<typeof SubscriptionBillingCycleSchema>;

export const SubscriptionStatusSchema = z.enum(['active', 'paused', 'cancelled']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const SUBSCRIPTION_CATEGORIES = [
  'hosting',
  'db',
  'ia',
  'ofimatica',
  'dominio',
  'pagos',
  'marketing',
  'otros',
] as const;
export const SubscriptionCategorySchema = z.enum(SUBSCRIPTION_CATEGORIES);
export type SubscriptionCategory = z.infer<typeof SubscriptionCategorySchema>;

export const SubscriptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  vendor: z.string().nullable(),
  category: z.string(),
  billingCycle: SubscriptionBillingCycleSchema,
  amount: z.number(),
  currency: z.string(),
  dashboardUrl: z.string().nullable(),
  billingDay: z.number().int().nullable(),
  nextChargeDate: IsoDateSchema.nullable(),
  status: SubscriptionStatusSchema,
  paymentMethod: z.string().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdByCode: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

export const CreateSubscriptionSchema = z.object({
  name: z.string().min(1).max(200),
  vendor: z.string().max(200).nullish(),
  category: SubscriptionCategorySchema.default('otros'),
  billingCycle: SubscriptionBillingCycleSchema.default('monthly'),
  amount: z.number().nonnegative(),
  currency: z.string().min(1).max(8).default('EUR'),
  dashboardUrl: z.string().url().max(500).nullish().or(z.literal('')),
  billingDay: z.number().int().min(1).max(31).nullish(),
  nextChargeDate: IsoDateSchema.nullish(),
  status: SubscriptionStatusSchema.default('active'),
  paymentMethod: z.string().max(200).nullish(),
  notes: z.string().max(1000).nullish(),
});
export type CreateSubscriptionRequest = z.infer<typeof CreateSubscriptionSchema>;

export const UpdateSubscriptionSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  vendor: z.string().max(200).nullish(),
  category: SubscriptionCategorySchema.optional(),
  billingCycle: SubscriptionBillingCycleSchema.optional(),
  amount: z.number().nonnegative().optional(),
  currency: z.string().min(1).max(8).optional(),
  dashboardUrl: z.string().url().max(500).nullish().or(z.literal('')),
  billingDay: z.number().int().min(1).max(31).nullish(),
  nextChargeDate: IsoDateSchema.nullish(),
  status: SubscriptionStatusSchema.optional(),
  paymentMethod: z.string().max(200).nullish(),
  notes: z.string().max(1000).nullish(),
});
export type UpdateSubscriptionRequest = z.infer<typeof UpdateSubscriptionSchema>;

export const SubscriptionsSummarySchema = z.object({
  activeCount: z.number(),
  monthlyByCurrency: z.record(z.string(), z.number()),
  annualByCurrency: z.record(z.string(), z.number()),
});
export type SubscriptionsSummary = z.infer<typeof SubscriptionsSummarySchema>;
